// (c) chaehongee. Confidential and proprietary.

import React, { Suspense, useEffect, useState } from 'react'
import { Layout } from './components/Layout/Layout'
import { Tree } from './components/Tree/Tree'
import { Canvas } from './components/Canvas/Canvas'
import { Backstage } from './components/Backstage/Backstage'
import { Sessions } from './components/Landing/Sessions'
import { Landing } from './components/Landing/Landing'
import { PrivacyPolicy } from './components/PrivacyPolicy/PrivacyPolicy'
import { TermsOfService } from './components/TermsOfService/TermsOfService'
import { LoginPage } from './components/Auth/LoginPage'
import { AuthCallback } from './components/Auth/AuthCallback'
import { Dashboard } from './components/Dashboard'
import { StripeCheckoutCallback } from './components/Checkout/StripeCheckoutCallback'
import { SubscriptionCheckoutCallback } from './components/Checkout/SubscriptionCheckoutCallback'
import { PricingPage } from './components/Pricing/PricingPage'
import { NotFound } from './components/NotFound/NotFound'

const FeatureWorkflow = React.lazy(() => import('./components/Marketing/FeatureWorkflow').then(m => ({ default: m.FeatureWorkflow })))
const CompareAlternative = React.lazy(() => import('./components/Marketing/CompareAlternative').then(m => ({ default: m.CompareAlternative })))
const UseCaseConceptArt = React.lazy(() => import('./components/Marketing/UseCaseConceptArt').then(m => ({ default: m.UseCaseConceptArt })))
const CompareVsMidjourney = React.lazy(() => import('./components/Marketing/CompareVsMidjourney').then(m => ({ default: m.CompareVsMidjourney })))
import { useAppSelector } from './store/hooks/hooks'
import { useGlobalPasteUpload } from './store/hooks/useGlobalPasteUpload'
import { useResetOnHomeNavigation } from './store/hooks/useResetOnHomeNavigation'
import { MaskProvider } from './components/Canvas/MaskContext'
import { useDeepLinkSessionLoader } from './store/hooks/useDeepLinkSessionLoader'
import { useAuth } from './store/hooks/useAuth'
import { useSessionTabsPersistence } from './store/hooks/useSessionTabsPersistence'
import { selectActiveSessionId } from './store/selectors/sessionSelectors'
import { useEnsureActiveSessionHydrated } from './store/hooks/useEnsureActiveSessionHydrated'
import './App.css'

function App() {
  const activeSessionId = useAppSelector(selectActiveSessionId)
  const { user, loading: authLoading } = useAuth()
  const [showLoginOverlay, setShowLoginOverlay] = useState(false)
  const [loginReturnUrl, setLoginReturnUrl] = useState<string | null>(null)
  
  // Track current path to trigger re-renders on navigation
  const [pathname, setPathname] = useState(window.location.pathname)

  useGlobalPasteUpload()
  useResetOnHomeNavigation()
  useSessionTabsPersistence()
  useDeepLinkSessionLoader()
  useEnsureActiveSessionHydrated()

  // Listen for login overlay trigger event
  useEffect(() => {
    const handleShowLogin = (e: Event) => {
      const customEvent = e as CustomEvent<{ returnUrl?: string }>
      const returnUrl = customEvent.detail?.returnUrl || window.location.pathname
      setLoginReturnUrl(returnUrl)
      setShowLoginOverlay(true)
    }

    window.addEventListener('show:login', handleShowLogin as EventListener)
    return () => {
      window.removeEventListener('show:login', handleShowLogin as EventListener)
    }
  }, [])

  // Listen for browser navigation (back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setPathname(window.location.pathname)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Handle routing based on initial pathname
  useEffect(() => {
    const currentPath = window.location.pathname

    // Handle /auth/callback route (OAuth redirect)
    if (currentPath === '/auth/callback') {
      // AuthCallback component handles the redirect
      return
    }

    // Handle /login route - show overlay and update returnUrl from query params
    if (currentPath === '/login') {
      const params = new URLSearchParams(window.location.search)
      const returnUrl = params.get('returnUrl') || currentPath
      setLoginReturnUrl(returnUrl)
      setShowLoginOverlay(true)
      // Update URL without navigation
      window.history.replaceState({}, '', returnUrl)
      setPathname(returnUrl)
      return
    }

    // Normalize legacy landing route to canonical root.
    if (currentPath === '/home') {
      window.history.replaceState({}, '', '/')
      setPathname('/')
      return
    }

  }, [])

  // Handle root path navigation based on authentication
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return
  }, [user, authLoading, pathname])

  // Show auth callback page
  if (pathname === '/auth/callback') {
    return <AuthCallback />
  }

  // Show privacy policy page
  if (pathname === '/privacy') {
    return <PrivacyPolicy />
  }

  // Show terms of service page
  if (pathname === '/terms') {
    return <TermsOfService />
  }

  // Stripe checkout callback page (restores previous in-app context)
  if (pathname === '/checkout/stripe') {
    return <StripeCheckoutCallback />
  }

  // Subscription checkout callback
  if (pathname === '/checkout/subscription') {
    return <SubscriptionCheckoutCallback />
  }

  // Pricing page
  if (pathname === '/pricing') {
    return <PricingPage />
  }

  // Show dashboard page
  if (pathname === '/dashboard') {
    return <Dashboard />
  }

  // Show landing page (public homepage)
  if (pathname === '/' && !user && !authLoading) {
    return <Landing onStart={() => window.location.href = '/sessions'} />
  }

  // Marketing pages (public, lazy-loaded)
  const marketingRoutes: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
    '/features/workflow': FeatureWorkflow,
    '/compare/midjourney-alternative': CompareAlternative,
    '/compare/vs-midjourney': CompareVsMidjourney,
    '/use-cases/concept-art': UseCaseConceptArt,
  }

  const MarketingComponent = marketingRoutes[pathname]
  if (MarketingComponent) {
    return (
      <Suspense fallback={null}>
        <MarketingComponent />
      </Suspense>
    )
  }

  // 404 for unknown public routes (not authenticated)
  const knownRoutes = ['/', '/sessions', '/dashboard', '/checkout/stripe', '/checkout/subscription', '/pricing']
  const knownPrefixes = ['/session/']
  const isKnownRoute =
    knownRoutes.includes(pathname) ||
    knownPrefixes.some((prefix) => pathname.startsWith(prefix))

  if (!isKnownRoute && !user && !authLoading) {
    return <NotFound />
  }

  // Handle closing login overlay
  const handleCloseLogin = () => {
    setShowLoginOverlay(false)
    setLoginReturnUrl(null)
  }

  // Determine main content to show
  let mainContent: React.ReactNode

  // Sessions list should win even if tabs are open
  if (pathname === '/sessions') {
    mainContent = <Sessions />
  } else if (activeSessionId) {
    // If there's an active tab (even if not hydrated yet), show the Layout
    mainContent = (
      <div className="app">
        <MaskProvider>
          <Layout
            treePanel={<Tree />}
            canvasPanel={<Canvas />}
            threadPanel={<Backstage />}
          />
        </MaskProvider>
      </div>
    )
  } else {
    // No active canvas session
    
    // At root path: show Sessions if logged in, otherwise redirect to /home (handled in useEffect)
    if (pathname === '/') {
      // Wait for auth to load, then show Sessions if logged in
      if (authLoading) {
        mainContent = null // Show nothing while loading
      } else if (user) {
        mainContent = <Sessions />
      } else {
        mainContent = null
      }
    } else {
      // Other paths without session - show Sessions
      mainContent = <Sessions />
    }
  }

  return (
    <>
      {/* Main content - always rendered */}
      {mainContent}

      {/* Login overlay - shown on top of any content */}
      {showLoginOverlay && (
        <LoginPage
          onClose={handleCloseLogin}
          returnUrl={loginReturnUrl || undefined}
        />
      )}
    </>
  )
}

export default App
